import {
    Box,
    Grid,
    Typography,
    TextField,
    MenuItem,
    Button,
    Card,
    CardContent,
    Alert,
    Divider,
    Paper,
    Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import useTitle from "hooks/useTitle";
import { McaLateFeeCalculators } from "assets";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import DescriptionIcon from "@mui/icons-material/Description";
import { number } from "yup";

const feeStructure: any = {
    "AOC-4 / AOC-4 CFS": {
        normalFee: (capital: number) => {
            if (capital === 0) return 200;
            if (capital <= 100000) return 200;
            if (capital <= 500000) return 300;
            if (capital <= 2500000) return 400;
            if (capital <= 10000000) return 500;
            if (capital > 10000000) return 600;
            return 600;
        },
        lateFeePerDay: 100,
        type: "daily",
    },
    "MGT-7 / MGT-7A": {
        normalFee: (capital: number) => {
            if (capital === 0) return 200;
            if (capital <= 100000) return 200;
            if (capital <= 500000) return 300;
            if (capital <= 2500000) return 400;
            if (capital <= 10000000) return 500;
            if (capital > 10000000) return 600;
            return 600;
        },
        lateFeePerDay: 100,
        type: "daily",
    },
    "FC-3": {
        normalFee: () => 6000,
        getMultiplier: (days: number) => {
            if (days <= 0) return 0;
            if (days <= 30) return 2;
            if (days <= 60) return 4;
            if (days <= 90) return 6;
            if (days <= 180) return 10;
            return 12;
        },
        type: "multiplier",
    },
    "FC-4": {
        normalFee: () => 6000,
        getMultiplier: (days: number) => {
            if (days <= 0) return 0;
            if (days <= 30) return 2;
            if (days <= 60) return 4;
            if (days <= 90) return 6;
            if (days <= 180) return 10;
            return 12;
        },
        type: "multiplier",
    },
    "ADT-1": {
        normalFee: () => 400,
        getMultiplier: (days: number) => {
            if (days <= 0) return 0;
            if (days <= 30) return 2;
            if (days <= 60) return 4;
            if (days <= 90) return 6;
            if (days <= 180) return 10;
            return 12;
        },
        type: "multiplier",
    },
    // "FC-3": {
    //     normalFee: () => 6000,
    //     lateFeePerDay: 100,
    //     type: "daily",
    // },
    // "FC-4": {
    //     normalFee: () => 6000,
    //     lateFeePerDay: 100,
    //     type: "daily",
    // },
    "MSC-1": {
        type: "fixed",
        getFee: (capital: number, companyType: string) => {

            if (companyType === "small" || companyType === "opc") {
                return { normal: 1000 };
            }

            if (capital <= 2500000) return { normal: 2000 };
            if (capital <= 5000000) return { normal: 5000 };
            if (capital <= 50000000) return { normal: 10000 };
            if (capital <= 100000000) return { normal: 15000 };
            return { normal: 20000 };
        }
    },

    "STK-2": {
        type: "fixed",
        getFee: (capital: number, companyType: string) => {
            return { normal: 10000 };
        }
    },


    // "DIR-3 KYC": {
    //     normalFee: () => 0,
    //     lateFeeFlat: 5000,
    //     type: "flat",
    // },

    // "MGT-14": {
    //     normalFee: () => 200,
    //     lateFeePerDay: 100,
    //     type: "daily",
    // },

    // "Form-8 LLP": {
    //     normalFee: () => 200,
    //     lateFeePerDay: (days: number, isSmall: boolean) => {
    //         if (isSmall) {
    //             if (days <= 100) return 10;
    //             if (days <= 300) return 20;
    //             return 50;
    //         }
    //         return 100;
    //     },
    //     type: "llp",
    // },
    // "Form-11 LLP": {
    //     normalFee: () => 200,
    //     lateFeePerDay: (days: number, isSmall: boolean) => {
    //         if (isSmall) {
    //             if (days <= 100) return 10;
    //             if (days <= 300) return 20;
    //             return 50;
    //         }
    //         return 100;
    //     },
    //     type: "llp",
    // },
};

const companyTypes: Record<string, string> = {
    regular: "Private/Public Limited",
    small: "Small Company",
    opc: "One Person Company",
    "small-llp": "Small LLP",
    "regular-llp": "Regular LLP",
};

const LateFeeCalculator = () => {
    useTitle("ATOM Pro | MCA Late Fee Calculator");

    const [formType, setFormType] = useState("");
    const [companyType, setCompanyType] = useState("");
    const [capital, setCapital] = useState<number | "">("");
    const [baseDate, setBaseDate] = useState("");
    const [filingDate, setFilingDate] = useState("");
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState("");

    const calculate = () => {
        setError("");

        const isEventBased = formType === "MSC-1" || formType === "STK-2";
        const isCapitalRequired = ["AOC-4 / AOC-4 CFS", "MGT-7 / MGT-7A", "MSC-1"].includes(formType);
        const isCompanyTypeRequired = formType === "MSC-1";

        if (
            !formType ||
            !filingDate ||
            (isCapitalRequired && !capital) ||
            (isCompanyTypeRequired && !companyType) ||
            (!isEventBased && !baseDate)
        ) {
            setError("All fields are required");
            return;
        }

        const due =
            formType === "MSC-1" || formType === "STK-2"
                ? null
                : getDueDate(formType, baseDate);
        const filing = moment(filingDate, "DD-MM-YYYY").startOf("day");

        const rawDelay = due ? filing.diff(due, "days") : 0;
        const delayDays = rawDelay > 0 ? rawDelay : 0;

        const startDate = new Date(2026, 3, 15);
        const endDate = new Date(2026, 6, 15, 23, 59, 59);

        let isWithinCCFSPeriod = false;

        if (filingDate) {
            const [day, month, year] = filingDate.split("-");

            const parsedDate = new Date(
                Number(year),
                Number(month) - 1,
                Number(day)
            );

            isWithinCCFSPeriod =
                parsedDate >= startDate && parsedDate <= endDate;
        }


        const fees = feeStructure[formType];

        let normalFee = 0;
        let lateFee = 0;
        let total = 0;
        let note = "";

        let discount = formType === "STK-2" ? 0.25 : formType === "MSC-1" ? 0.5 : 0.1;
        let discountAmount = 0;
        let finalTotal = 0;
        let normalFeeAfterDiscount = 0;
        let lateFeeAfterDiscount = 0;
        let savePercent = 0;
        let duedateneeded = false;

        if (fees.type === "fixed") {

            const slab = fees.getFee(capital, companyType);

            normalFee = slab.normal;
            total = normalFee;

            finalTotal = normalFee * discount;
            discountAmount = total - finalTotal;
            normalFeeAfterDiscount = normalFee * discount;
            savePercent = (1 - discount) * 100;


            note = `Normal Fee: ₹${slab.normal} `;

        } else if (fees.type === "multiplier") {

            normalFee =
                typeof fees.normalFee === "function"
                    ? fees.normalFee(capital)
                    : fees.normalFee;

            const multiplier = fees.getMultiplier(delayDays);
            lateFee = normalFee * multiplier;
            duedateneeded = true;

            total = normalFee + lateFee;
            normalFeeAfterDiscount = normalFee;
            lateFeeAfterDiscount = lateFee * discount;

            finalTotal = lateFeeAfterDiscount + normalFee;
            discountAmount = total - finalTotal;
            savePercent = total > 0 ? (discountAmount / total) * 100 : 0;

            note =
                delayDays === 0
                    ? "Filed on time"
                    : `Normal Fee: ₹${normalFee} | Additional Fee: ${multiplier}× Normal Fee`;

        } else {

            let perDayRate =
                typeof fees.lateFeePerDay === "function"
                    ? fees.lateFeePerDay(capital)
                    : fees.lateFeePerDay;

            lateFee = delayDays * perDayRate;
            duedateneeded = true;

            normalFee =
                typeof fees.normalFee === "function"
                    ? fees.normalFee(capital)
                    : 0;

            total = normalFee + lateFee;
            normalFeeAfterDiscount = normalFee;
            lateFeeAfterDiscount = lateFee * discount;


            finalTotal = lateFeeAfterDiscount + normalFee;
            discountAmount = total - finalTotal;
            savePercent = (discountAmount / total) * 100;

            note =
                delayDays === 0
                    ? "Filed on time"
                    : `Normal Fee: ₹${normalFee} | Additional Fee: ₹${perDayRate}/day × ${delayDays} days`;
        }

        // else if (fees.type === "flat") {
        //     lateFee = fees.lateFeeFlat;
        //     note = `Flat penalty ₹${fees.lateFeeFlat}`;
        // } else if (fees.type === "llp") {
        //     const isSmall = companyType === "small-llp";
        //     const rate = fees.lateFeePerDay(delayDays, isSmall);
        //     lateFee = delayDays * rate;
        //     note = `₹${rate}/day × ${delayDays} days`;
        // }

        setResult({
            duedateneeded,
            delayDays,
            normalFee,
            lateFee,
            total: normalFee + lateFee,
            note,
            savePercent,
            discountAmount,
            finalTotal,
            normalFeeAfterDiscount,
            lateFeeAfterDiscount,
            isWithinCCFSPeriod,
            dueDate: due ? due.format("DD-MM-YYYY") : null,
        });
    };

    const glassField = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
            background: "rgba(255,255,255,0.6)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.8)",
            transition: "all 0.3s ease",
            "&:hover": {
                background: "rgba(255,255,255,0.75)",
            },
            "& fieldset": {
                border: "none",
            },
        },
        "& .MuiInputLabel-root": {
            fontWeight: 500,
        },
    };
    const darkGlassCard = {
        borderRadius: "26px",
        position: "relative",
        overflow: "hidden",
        color: "#0f172a",

        background: `
      radial-gradient(circle at 85% 15%, rgba(80, 229, 255, 0.18), transparent 45%),
      linear-gradient(135deg, #f8fbff 0%, #eef5ff 50%, #e3efff 100%)
  `,

        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",

        border: "1px solid rgba(255,255,255,0.6)",

        boxShadow: `
      0 20px 50px rgba(30, 64, 175, 0.10),
      inset 0 1px 0 rgba(255,255,255,0.8)
  `,

        transition: "all 0.3s ease",
    };


    const miniDarkBox = {
        p: 2,
        borderRadius: "16px",
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.6)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
    };

    const lightCard = {
        p: 1.5,
        borderRadius: "16px",
        background: "#ffffff",
        border: "1px solid #eef2f7",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        display: "flex",
        alignItems: "center",
        gap: 2,
        transition: "0.3s",
        "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
        },
    };

    const iconBox = (bg: string) => ({
        width: 42,
        height: 42,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: bg,
        fontSize: "20px",
    });


    const formBox = {
        p: 1.1,
        borderRadius: "16px",
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.7)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
    };

    const noteBox = {
        p: 3,
        borderRadius: "18px",
        background: "rgba(255, 183, 77, 0.12)",
        border: "1px solid rgba(255, 183, 77, 0.35)",
        color: "#b45309",
        backdropFilter: "blur(12px)",
    };

    const FeeRow = ({ label, value }: any) => (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1.2,
            }}
        >
            <Typography color="text.secondary">{label}</Typography>
            <Typography fontWeight={600}>₹{value}</Typography>
        </Box>
    );
    const formatINR = (amount: number) => {
        return new Intl.NumberFormat("en-IN").format(amount);
    };
    const getDateLabel = () => {
        switch (formType) {
            case "AOC-4 / AOC-4 CFS":
            case "MGT-7 / MGT-7A":
                return "AGM Date";
            case "ADT-1":
                return "Date of Appointment";

            case "FC-3":
                return "Balance Sheet End Date";
            case "FC-4":
                return "Balance Sheet End Date";
            default:
                return "";
        }
    };
    const getDueDate = (formType: string, baseDate: string) => {
        const date = moment(baseDate, "DD-MM-YYYY");

        switch (formType) {
            case "AOC-4 / AOC-4 CFS":
                return date.clone().add(30, "days").subtract(1, "day");

            case "MGT-7 / MGT-7A":
                return date.clone().add(60, "days");

            case "ADT-1":
                return date.clone().add(15, "days").subtract(1, "day");

            case "FC-3":
                return date.clone().add(6, "months").subtract(1, "day");
            case "FC-4":
                return date.clone().add(15, "days").subtract(1, "day");

            default:
                return null;
        }
    };

    useEffect(() => {
        setResult(null);
    }, [formType, capital, baseDate, filingDate, companyType]);


    const isBeforeDeadline = new Date() < new Date("2026-07-15T23:59:59");

    return (
        <Box m={3} >
            <Grid item xs={12} mb={4}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        borderRadius: "26px",
                        position: "relative",
                        overflow: "hidden",
                        color: "#0f172a",
                        background: `
                            radial-gradient(circle at 85% 15%, rgba(80, 229, 255, 0.25), transparent 40%),
                            linear-gradient(135deg, #f8fbff 0%, #eef5ff 50%, #e3efff 100%)
                        `,
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",

                        border: "1px solid rgba(255, 255, 255, 0.6)",

                        boxShadow: `
                            0 20px 50px rgba(30, 64, 175, 0.12),
                            inset 0 1px 0 rgba(255,255,255,0.8)
                        `,

                        transition: "all 0.3s ease",

                        "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: `
                                0 30px 70px rgba(30, 64, 175, 0.18)
                            `,
                        },
                    }}
                >
                    <Stack spacing={2.5}>

                        <Stack direction="row" spacing={1} alignItems="center">

                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: "12px",
                                    display: "flex",
                                    marginTop: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backdropFilter: "blur(18px)",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={McaLateFeeCalculators}
                                    alt="MCA Logo"
                                    sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                />
                            </Box>

                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    letterSpacing: "-0.5px",
                                }}
                            >
                                MCA Late Fee Calculator
                            </Typography>
                        </Stack>

                        <Typography
                            variant="body1"
                            sx={{
                                color: "rgba(15,23,42,0.7)",
                                lineHeight: 1.6,
                            }}
                        >
                            Compare normal MCA late fees with CCFS-2026 discounted fees
                            side-by-side. Calculate savings instantly.
                        </Typography>

                        {isBeforeDeadline && (
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                pt={1}
                            >
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 3,
                                        py: 1.2,
                                        borderRadius: "14px",
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        color: "#b45309",
                                        background: "rgba(255, 183, 77, 0.25)",
                                        backdropFilter: "blur(12px)",
                                        border: "1px solid rgba(255, 183, 77, 0.5)",
                                        boxShadow: "0 6px 18px rgba(255,183,77,0.25)",
                                    }}
                                >
                                    ⚡ CCFS-2026: 15 Apr – 15 Jul 2026
                                </Box>

                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 3,
                                        py: 1.2,
                                        borderRadius: "14px",
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                        color: "#047857",
                                        background: "rgba(52, 211, 153, 0.25)",
                                        backdropFilter: "blur(12px)",
                                        border: "1px solid rgba(52, 211, 153, 0.5)",
                                        boxShadow: "0 6px 18px rgba(52,211,153,0.25)",
                                    }}
                                >
                                    💰 Save up to 90% on late fees
                                </Box>

                            </Stack>
                        )}
                    </Stack>
                </Paper>
            </Grid>


            <Grid
                container
                spacing={1}
                mb={4}
                sx={{
                    p: 2,
                    borderRadius: "24px",
                    background: "linear-gradient(135deg, #f8fbff, #eef3ff)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.6)",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Grid item xs={12} md={4} p={1}>
                    <TextField
                        select
                        fullWidth
                        label="Form Type"
                        value={formType}
                        onChange={(e) => setFormType(e.target.value)}
                        sx={glassField}
                    >
                        {Object.keys(feeStructure).map((form) => (
                            <MenuItem key={form} value={form}>
                                {form}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                {["AOC-4 / AOC-4 CFS", "MGT-7 / MGT-7A", "MSC-1"].includes(formType) && (
                    <Grid item xs={12} md={4} p={1}>
                        <TextField
                            fullWidth
                            label="Authorized Capital"
                            value={capital}
                            onChange={(e) => {
                                const value = e.target.value;

                                // Allow only digits OR empty
                                if (/^\d*$/.test(value)) {
                                    setCapital(value === "" ? "" : Number(value));
                                }
                            }}
                            onKeyDown={(e) => {
                                // Block invalid keys
                                if (
                                    ["e", "E", "+", "-", ".", ","].includes(e.key)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                            onPaste={(e) => {
                                const paste = e.clipboardData.getData("text");

                                if (!/^\d+$/.test(paste)) {
                                    e.preventDefault();
                                }
                            }}
                            sx={glassField}
                            inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                            }}
                        />
                    </Grid>
                )}


                {formType === "MSC-1" && (
                    <Grid item xs={12} md={4} p={1}>
                        <TextField
                            select
                            fullWidth
                            label="Company Type"
                            value={companyType}
                            onChange={(e) => setCompanyType(e.target.value)}
                            sx={glassField}
                        >
                            {Object.keys(companyTypes).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {companyTypes[type]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}

                <LocalizationProvider dateAdapter={AdapterMoment}>
                    {!(formType === "STK-2" || formType === "MSC-1") && (
                        <Grid item xs={12} md={4}>
                            {!(formType === "MSC-1" || formType === "STK-2") && (
                                <DesktopDatePicker
                                    label={getDateLabel()}
                                    disabled={!formType}
                                    value={baseDate ? moment(baseDate, "DD-MM-YYYY") : null}
                                    onChange={(newValue) => {
                                        setBaseDate(newValue ? newValue.format("DD-MM-YYYY") : "");
                                    }}
                                    inputFormat="DD-MM-YYYY"
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth sx={glassField} />
                                    )}
                                />
                            )}
                        </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                        <DesktopDatePicker
                            label="Filing Date"
                            value={filingDate ? moment(filingDate, "DD-MM-YYYY") : null}
                            onChange={(newValue) => {
                                setFilingDate(newValue ? newValue.format("DD-MM-YYYY") : "");
                            }}
                            inputFormat="DD-MM-YYYY"
                            renderInput={(params) => (
                                <TextField {...params} fullWidth sx={glassField} />
                            )}
                        />
                    </Grid>
                </LocalizationProvider>

                <Grid item xs={12} md={8}>
                    <Box
                        onClick={calculate}
                        sx={{
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "16px",
                            fontWeight: 600,
                            fontSize: "16px",
                            letterSpacing: "0.5px",
                            cursor: "pointer",
                            background:
                                "linear-gradient(135deg, rgba(0,87,255,0.9), rgba(0,153,255,0.85))",
                            color: "#fff",
                            backdropFilter: "blur(12px)",
                            boxShadow: "0 10px 25px rgba(0, 87, 255, 0.3)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow: "0 15px 35px rgba(0, 87, 255, 0.45)",
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                            },
                        }}
                    >
                        Calculate
                    </Box>
                </Grid>
            </Grid>


            <Grid container spacing={4}>

                {error && (
                    <Grid item xs={12}>
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: "16px",
                                backdropFilter: "blur(10px)",
                            }}
                        >
                            {error}
                        </Alert>
                    </Grid>
                )}


                {result && (
                    result.isWithinCCFSPeriod ? (
                        <>
                            <Grid item xs={12} md={6}>
                                <Card sx={darkGlassCard}>
                                    <CardContent>

                                        <Typography variant="h5" fontWeight={700}>
                                            Normal Late Fee
                                        </Typography>
                                        <Typography color="text.secondary" mb={3}>
                                            Standard MCA filing fees
                                        </Typography>

                                        {result.dueDate && (
                                            <Grid container spacing={2} mb={3}>
                                                <Grid item xs={6}>
                                                    <Box sx={miniDarkBox}>
                                                        <Typography variant="caption">Due Date</Typography>
                                                        <Typography fontWeight={600}>
                                                            {result.dueDate || "N/A"}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box sx={miniDarkBox}>
                                                        <Typography variant="caption">
                                                            Delay (Days)
                                                        </Typography>
                                                        <Typography fontWeight={700} color="#00b894">
                                                            {result.delayDays}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}

                                        <FeeRow label="Normal Filing Fee" value={formatINR(result.normalFee)} />
                                        {result.lateFee > 0 && (
                                            <FeeRow
                                                label="Additional Fee"
                                                value={formatINR(result.lateFee)}
                                            />
                                        )}

                                        <Divider sx={{ my: 3 }} />
                                        <Box
                                            sx={{
                                                position: "relative",
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >

                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Total Fee
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                fontWeight={800}
                                                sx={{ color: "#4d88ff" }}
                                            >
                                                ₹{formatINR(result.total)}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignText: "center",
                                                justifyContent: "center",
                                                gap: 1,
                                                px: 3,
                                                py: 1.2,
                                                borderRadius: "14px",
                                                fontWeight: 600,
                                                fontSize: "0.95rem",
                                                color: "#b45309",
                                                background: "rgba(255, 183, 77, 0.25)",
                                                backdropFilter: "blur(12px)",
                                                border: "1px solid rgba(255, 183, 77, 0.5)",
                                                boxShadow: "0 6px 18px rgba(255,183,77,0.25)",
                                                mt: 1,
                                            }}
                                        >
                                            ⚠️ Without CCFS-2026 Benefits
                                        </Box>

                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        ...darkGlassCard,
                                        color: "#047857",
                                        background: "rgba(52, 211, 153, 0.25)",
                                        backdropFilter: "blur(12px)",
                                        border: "1px solid rgba(52, 211, 153, 0.5)",
                                        boxShadow: "0 6px 18px rgba(52,211,153,0.25)",
                                    }}
                                >
                                    <CardContent>

                                        <Typography variant="h5" fontWeight={700}>
                                            CCFS-2026 Fee
                                        </Typography>
                                        <Typography color="text.secondary" mb={3}>
                                            With Discount late fee waiver
                                        </Typography>

                                        <FeeRow
                                            label="Normal Fee (CCFS)"
                                            value={formatINR(result.normalFeeAfterDiscount)}
                                        />

                                        {result.lateFee > 0 && (
                                            <FeeRow
                                                label="Additional Fee (90% Waived)"
                                                value={formatINR(result.lateFeeAfterDiscount)}
                                            />
                                        )}

                                        <Divider sx={{ my: 3 }} />
                                        <Box
                                            sx={{
                                                position: "relative",
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Total Fee
                                            </Typography>

                                            <Typography
                                                variant="h4"
                                                fontWeight={800}
                                                sx={{ color: "#00b894" }}
                                            >
                                                ₹{formatINR(result.finalTotal)}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Box
                                    sx={{
                                        mt: 3,
                                        px: 4,
                                        py: 3,
                                        borderRadius: "18px",
                                        position: "relative",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",

                                        background: "linear-gradient(135deg, #7b5cff 0%, #4c8df6 100%)",
                                        color: "#fff",

                                        boxShadow: "0 18px 45px rgba(76,141,246,0.35)",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                opacity: 0.85,
                                                fontWeight: 500,
                                                mb: 0.5,
                                            }}
                                        >
                                            You Save
                                        </Typography>

                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            sx={{
                                                letterSpacing: "-1px",
                                            }}
                                        >
                                            ₹{formatINR(result.total - result.finalTotal)}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: 90,
                                            height: 90,
                                            borderRadius: "50%",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",

                                            background: "rgba(255,255,255,0.18)",
                                            backdropFilter: "blur(10px)",
                                            border: "1px solid rgba(255,255,255,0.35)",

                                            boxShadow: "inset 0 4px 12px rgba(255,255,255,0.25)",
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight={800}>
                                            {result.savePercent.toFixed(0)}%
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.85, fontWeight: 500 }}
                                        >
                                            OFF
                                        </Typography>
                                    </Box>
                                </Box>

                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} md={12}>
                            <Card
                                sx={{
                                    borderRadius: "24px",
                                    p: 1,
                                    background: "linear-gradient(145deg, #ffffff, #f4f8ff)",
                                    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
                                    backdropFilter: "blur(12px)",
                                    border: "1px solid rgba(255,255,255,0.6)",
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>

                                    <Box mb={3}>
                                        <Typography variant="h5" fontWeight={800}>
                                            Normal Late Fee
                                        </Typography>
                                        <Typography color="text.secondary">
                                            Standard MCA filing fees
                                        </Typography>
                                    </Box>

                                    {result.dueDate && (
                                        <Grid container spacing={2} mb={4}>
                                            <Grid item xs={6}>
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: "18px",
                                                        background: "rgba(77,136,255,0.08)",
                                                        border: "1px solid rgba(77,136,255,0.15)",
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Due Date
                                                    </Typography>
                                                    <Typography fontWeight={700} fontSize="1rem">
                                                        {result.dueDate || "N/A"}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: "18px",
                                                        background: "rgba(0,184,148,0.08)",
                                                        border: "1px solid rgba(0,184,148,0.2)",
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Delay (Days)
                                                    </Typography>
                                                    <Typography fontWeight={800} fontSize="1.1rem" color="#00b894">
                                                        {result.delayDays}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Box
                                        sx={{
                                            p: 3,
                                            borderRadius: "20px",
                                            background: "#ffffff",
                                            boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                                        }}
                                    >
                                        <FeeRow label="Normal Filing Fee" value={formatINR(result.normalFee)} />

                                        {result.lateFee > 0 && (
                                            <FeeRow
                                                label="Additional Fee (₹100/day)"
                                                value={formatINR(result.lateFee)}
                                            />
                                        )}
                                    </Box>

                                    <Divider sx={{ my: 4 }} />

                                    <Box
                                        sx={{
                                            p: 3,
                                            borderRadius: "20px",
                                            background: "linear-gradient(135deg, #4d88ff, #6fa8ff)",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            boxShadow: "0 12px 30px rgba(77,136,255,0.35)",
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            Total Fee
                                        </Typography>

                                        <Typography variant="h4" fontWeight={900}>
                                            ₹{formatINR(result.total)}
                                        </Typography>
                                    </Box>

                                </CardContent>
                            </Card>
                        </Grid>
                    )
                )}
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: "flex",
                            alignText: "center",
                            justifyContent: "center",
                            gap: 1,
                            px: 3,
                            py: 1.2,
                            borderRadius: "14px",
                            // fontWeight: 600,
                            fontSize: "0.95rem",
                            color: "#b45309",
                            background: "rgba(255, 183, 77, 0.25)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(255, 183, 77, 0.5)",
                            boxShadow: "0 6px 18px rgba(255,183,77,0.25)",
                            mt: 1,
                        }}
                    >
                        <strong>Disclaimer: </strong>These calculations are indicative and for general guidance only. Actual fees are subject to MCA portal validation. Normal filing fees apply in addition to late fees. CCFS-2026 benefits available only from 15 April 2026 to 15 July 2026.
                    </Box>
                </Grid>

                <Grid
                    container
                    mt={6}
                    sx={{
                        p: 4,
                        borderRadius: "28px",
                        mt: 6,
                        background: `
                            radial-gradient(circle at 85% 15%, rgba(80, 229, 255, 0.15), transparent 40%),
                            linear-gradient(135deg, #f8fbff 0%, #eef5ff 50%, #e3efff 100%)
                        `,
                        border: "1px solid rgba(255,255,255,0.6)",
                        boxShadow: "0 20px 50px rgba(30, 64, 175, 0.10)",
                    }}

                >

                    <Grid item xs={12} md={3.8} sx={{
                        p: 2,
                        borderRadius: "16px",
                        background: "rgba(105, 68, 239, 0.12)",
                        border: "1px solid rgba(105, 68, 239, 0.35)",
                        backdropFilter: "blur(12px)",
                        boxShadow: "0 8px 20px rgba(105, 68, 239, 0.1)",
                    }}>
                        <Typography variant="h5" fontWeight={800} mb={3}>
                            🎁 CCFS-2026 Benefits
                        </Typography>

                        <Stack spacing={2.5}>

                            <Box sx={lightCard}>
                                <Box sx={iconBox("#e6f9f1")}><DescriptionIcon sx={{ color: "#047857", fontSize: 24 }} /></Box>
                                <Box>
                                    <Typography fontWeight={600}>
                                        Annual Filing Forms
                                    </Typography>
                                    <Typography sx={{ color: "#10b981", fontWeight: 700 }}>
                                        90% off on late fees
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={lightCard}>
                                <Box sx={iconBox("#eaf2ff")}>😴</Box>
                                <Box>
                                    <Typography fontWeight={600}>
                                        Dormant Application
                                    </Typography>
                                    <Typography sx={{ color: "#3b82f6", fontWeight: 700 }}>
                                        50% off on filing fees
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={lightCard}>
                                <Box sx={iconBox("#f3ecff")}>⛔</Box>
                                <Box>
                                    <Typography fontWeight={600}>
                                        Strike-off Application
                                    </Typography>
                                    <Typography sx={{ color: "#8b5cf6", fontWeight: 700 }}>
                                        75% off on filing fees
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={lightCard}>
                                <Box sx={iconBox("#fff7e6")}>✅</Box>
                                <Box>
                                    <Typography fontWeight={600}>
                                        Penalty Immunity
                                    </Typography>
                                    <Typography sx={{ color: "#f59e0b", fontWeight: 700 }}>
                                        If filed before notice
                                    </Typography>
                                </Box>
                            </Box>

                        </Stack>
                    </Grid>


                    <Grid item xs={12} md={3.8} sx={{
                        mx: 3,
                        p: 2,
                        borderRadius: "16px",
                        background: "rgba(68, 139, 239, 0.12)",
                        border: "1px solid rgba(68, 128, 239, 0.35)",
                        backdropFilter: "blur(12px)",
                        boxShadow: "0 8px 20px rgba(68, 131, 239, 0.1)",
                    }}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            mb={3}
                        >
                            <DescriptionIcon
                                sx={{ color: "#3b82f6", fontSize: 34 }}
                            />
                            <Typography variant="h5" fontWeight={800}>
                                Forms Covered
                            </Typography>
                        </Box>


                        <Stack spacing={2}>
                            {[
                                "AOC-4 / AOC-4 CFS",
                                "MGT-7 / MGT-7A",
                                "ADT-1",
                                "FC-3",
                                "FC-4",
                                "MSC-1 (Dormant)",
                                "STK-2 (Strike-off)"
                            ].map((form) => (
                                <Box key={form} sx={{ ...formBox, color: "#2359fb", fontWeight: 600 }}>
                                    • {form}
                                </Box>
                            ))}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={3.8}>
                        <Box sx={noteBox}>
                            <Typography fontWeight={700} mb={1} >
                                ⚠️ Important Notes
                            </Typography>
                            <Stack spacing={0}>
                                <Typography>• Calculations are indicative only</Typography>
                                <Typography>• Normal filing fees still apply</Typography>
                                <Typography>• Subject to MCA validation</Typography>
                                <Typography>• Scheme valid till 15 July 2026</Typography>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                mt: 3,
                                p: 3,
                                borderRadius: "18px",
                                background: "rgba(239, 68, 68, 0.12)",
                                border: "1px solid rgba(239, 68, 68, 0.35)",
                                color: "#b91c1c",
                                backdropFilter: "blur(12px)",
                                boxShadow: "0 8px 20px rgba(239,68,68,0.1)",

                            }}
                        >
                            <Typography fontWeight={700} mb={1}>
                                🚫 Cannot Avail
                            </Typography>
                            <Typography variant="body2">
                                • Final strike-off notice issued<br />
                                • Already applied for strike-off<br />
                                • Dissolved / Vanishing companies
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

            </Grid>

        </Box>
    );
};

export default LateFeeCalculator;
